'use client';

// ChatGPT-matched conversation controls for the Master surface, measured pixel-for-pixel
// from chatgpt.com: a Share button (36px tall, radius 8, 14px/500, icon+label) and a
// 36px kebab that opens a dropdown (#353535, radius 16, 6px 0 padding, box shadow) with
// 36px items (14px/400, radius-10 hover pills inset 6px, hover #4A4A4A, destructive item
// in #FF6764). The commands are Fater's own: Share link, Pin/Unpin, Rename, Delete.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { Share, MoreHorizontal, Pin, PinOff, PencilLine, Trash2, Check } from '@/components/ui/lucide-memo';
import { toggleMasterSessionPin } from '@/lib/masterSessionPins';
import { confirmChatSessionDeletion } from '#lib/chat/chatSessionDeletion';

// ChatGPT item: 36px tall, gap 6, radius 10, left/right pad 10, 14px, hover #4A4A4A.
const ITEM_BASE =
  'flex h-9 w-full items-center gap-1.5 rounded-[10px] px-2.5 text-left text-[14px] transition-colors';

const CONTAINER_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: 12,
  right: 16,
  zIndex: 40,
  display: 'flex',
  alignItems: 'center',
  gap: 2,
};
const SHARE_STYLE: React.CSSProperties = { fontSize: 14, fontWeight: 500 };
const MENU_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: 42,
  right: 0,
  minWidth: 210,
  background: '#353535',
  borderRadius: 16,
  padding: '6px 0',
  boxShadow: '0 12px 32px rgba(0,0,0,0.45)',
  zIndex: 50,
};
// fontSize set here (not via Tailwind text-[14px], which does not emit against the global
// 12px base) so items inherit 14px through preflight's font-size:100%.
const MENU_LIST_STYLE: React.CSSProperties = {
  padding: '0 6px',
  display: 'flex',
  flexDirection: 'column',
  fontSize: 14,
};
const ITEM_STYLE: React.CSSProperties = { fontSize: 14 };

export function MasterConversationMenu() {
  const { kernel } = useKernel();
  const activeId = useKernelStore((s) => s.activeChatSessionId);
  const session = useKernelStore((s) =>
    s.activeChatSessionId ? s.inventory.ChatSession.registry[s.activeChatSessionId] ?? null : null,
  );
  const isMaster = !!session && session.creationModule === 'master';
  const hasConversation = isMaster && (session?.messageCount ?? 0) > 0;

  const [menuOpen, setMenuOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Pins live in User.preferences, so the store is the shared source: the label updates on its
  // own when the sidebar toggles the same session.
  const pinnedSessionIds = useKernelStore((s) => s.userPreferences.pinnedMasterSessionIds);
  const pinned = !!activeId && !!pinnedSessionIds?.includes(activeId);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const share = useCallback(() => {
    if (!activeId) return;
    try {
      const url = `${window.location.origin}${window.location.pathname}?c=${activeId}`;
      navigator.clipboard
        ?.writeText(url)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        })
        .catch(() => {});
    } catch {
      /* clipboard unavailable */
    }
  }, [activeId]);

  const togglePin = useCallback(() => {
    const k = kernel.current;
    if (activeId && k) toggleMasterSessionPin(k, activeId);
    setMenuOpen(false);
  }, [activeId, kernel]);

  const rename = useCallback(() => {
    setMenuOpen(false);
    if (!activeId) return;
    const current = session?.name?.trim() || '';
    const next = window.prompt('Rename chat:', current);
    if (next && next.trim() && next.trim() !== current) kernel.current?.renameChatSession(activeId, next.trim());
  }, [activeId, session, kernel]);

  const del = useCallback(() => {
    setMenuOpen(false);
    if (activeId) confirmChatSessionDeletion(kernel, activeId);
  }, [activeId, kernel]);

  const toggleMenu = useCallback(() => setMenuOpen((o) => !o), []);

  if (!hasConversation || !activeId) return null;

  return (
    <div ref={containerRef} style={CONTAINER_STYLE}>
      <button
        type="button"
        onClick={share}
        title="Copy a link to this chat"
        className="inline-flex h-9 items-center gap-1.5 rounded-lg px-3 text-white transition-colors hover:bg-[#2A2A2A]"
        style={SHARE_STYLE}
      >
        {copied ? <Check size={18} strokeWidth={2.2} /> : <Share size={18} strokeWidth={2} />}
        {copied ? 'Copied' : 'Share'}
      </button>
      <button
        type="button"
        onClick={toggleMenu}
        aria-label="Open conversation options"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-white transition-colors hover:bg-[#2A2A2A]"
      >
        <MoreHorizontal size={20} strokeWidth={2} />
      </button>

      {menuOpen && (
        <div
          role="menu"
          style={MENU_STYLE}
        >
          <div style={MENU_LIST_STYLE}>
            <button type="button" role="menuitem" onClick={togglePin} className={`${ITEM_BASE} text-white hover:bg-[#4A4A4A]`} style={ITEM_STYLE}>
              {pinned ? <PinOff size={20} strokeWidth={1.9} /> : <Pin size={20} strokeWidth={1.9} />}
              {pinned ? 'Unpin chat' : 'Pin chat'}
            </button>
            <button type="button" role="menuitem" onClick={rename} className={`${ITEM_BASE} text-white hover:bg-[#4A4A4A]`} style={ITEM_STYLE}>
              <PencilLine size={20} strokeWidth={1.9} />
              Rename
            </button>
            <button type="button" role="menuitem" onClick={del} className={`${ITEM_BASE} text-[#FF6764] hover:bg-[#4A4A4A]`} style={ITEM_STYLE}>
              <Trash2 size={20} strokeWidth={1.9} />
              Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
